import React, { useState } from 'react';
import { arrayOf, number, shape, string } from 'prop-types';
import classNames from 'classnames';
import { withDimensions } from '../../util/uiHelpers';

import Handle from './Handle';
import Track from './Track';
import css from './RangeSlider.module.css';

const RangeSliderComponent = props => {
  const { min = 0, max = 10000000, handles, dimensions, step = 1 } = props;
  handles.forEach((h, index) => {
    if (h < min || h > max || (index < handles.length - 1 && h > handles[index + 1])) {
      throw new Error(
        'RangeSlider error: handles need to be given in ascending order and they need to be within min and max values'
      );
    }
  });
  const [activeHandle, setActiveHandle] = useState(0);

  const toPosition = value => {
    const width = dimensions.width;
    const valueOffset = value - min;
    const scale = max - min;
    return Math.round((valueOffset / scale) * width);
  };

  const toValue = position => {
    const width = dimensions.width;
    const scale = max - min;
    const value = Math.round((position / width) * scale) + min;
    return Math.ceil(value / step) * step;
  };

  const changeActive = index => {
    setActiveHandle(index);
  };

  const onChange = (position, handleIndex) => {
    props.onChange(Object.assign([...handles], { [handleIndex]: position }));
  };

  return (
    <Track handles={handles} valueToPosition={toPosition}>
      {handles.map((h, index) => {
        const classes = classNames({ [css.activeHandle]: activeHandle === index });
        return (
          <Handle
            key={index}
            className={classes}
            value={h}
            min={index === 0 ? min : handles[index - 1]}
            max={index === handles.length - 1 ? max : handles[index + 1]}
            valueToPosition={toPosition}
            positionToValue={toValue}
            changeActive={() => changeActive(index)}
            onChange={value => onChange(value, index)}
          />
        );
      })}
    </Track>
  );
};

RangeSliderComponent.propTypes = {
  handles: arrayOf(number),
  min: number,
  max: number,
  step: number,
  dimensions: shape({
    height: number.isRequired,
    width: number.isRequired,
  }).isRequired,
};

const RangeSliderComponentWithDimensions = withDimensions(RangeSliderComponent);

const RangeSlider = props => {
  const { rootClassName = null, className = null, ...rest } = props;
  const classes = classNames(rootClassName || css.root, className);
  return (
    <div className={classes}>
      <RangeSliderComponentWithDimensions {...rest} />
    </div>
  );
};

RangeSlider.propTypes = {
  rootClassName: string,
  className: string,
};

export default RangeSlider;
